import { useQuery } from "@tanstack/react-query";
import { MessagingStatus } from "@/components/realtime/messaging-status";
import { Clock } from "lucide-react";

import { orpc } from "@/client";
import type { SystemJobsOverview } from "@/api/db/system-jobs";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonRows } from "@/components/ui/skeleton";
import { OperacaoLabels } from "@/lib/labels/operacao";
import { SystemLabels } from "@/lib/labels/system";

import { SectionCard } from "./section-card";

export function JobsSection() {
	const { data, isLoading, isError } = useQuery(orpc.system.jobs.queryOptions());

	return (
		<SectionCard title={SystemLabels.sections.jobs} dataComponent="system-jobs">
			{isLoading && <SkeletonRows rows={4} />}

			{!isLoading && isError && (
				<p className="text-sm text-destructive">{SystemLabels.loadError}</p>
			)}

			{!isLoading && !isError && data !== undefined && (
				<div className="flex flex-col gap-4">
					<ScheduledList jobs={data.scheduled} />

					<MessagingRow messaging={data.messaging} />

					<PipelineList pipeline={data.pipeline} />
				</div>
			)}
		</SectionCard>
	);
}

function ScheduledList({ jobs }: { jobs: SystemJobsOverview["scheduled"] }) {
	if (jobs.length === 0) {
		return <EmptyState icon={Clock} title={SystemLabels.empty.jobs} />;
	}

	return (
		<ul className="flex flex-col divide-y divide-border">
			{jobs.map((job) => (
				<li
					key={job.name}
					data-slot="job"
					data-job={job.name}
					className="flex items-center justify-between gap-3 py-2 first:pt-0"
				>
					<span className="text-sm text-foreground">{SystemLabels.jobName(job.name)}</span>

					<span className="text-xs text-muted-foreground">{SystemLabels.cadence(job)}</span>
				</li>
			))}
		</ul>
	);
}

function MessagingRow({ messaging }: { messaging: SystemJobsOverview["messaging"] }) {
	const lastDiscovery = messaging.last_discovery_at
		? OperacaoLabels.moment(messaging.last_discovery_at)
		: "Ainda não realizada";
	return (
		<div data-slot="messaging" className="rounded-md border border-border bg-background/50 p-3">
			<div className="flex items-center justify-between gap-2">
				<p className="text-xs font-medium text-foreground">Recebimento e recuperação</p>
				<MessagingStatus />
			</div>
			<div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
				<span>Última descoberta: {lastDiscovery}</span>
				<span>
					{messaging.pending} eventos pendentes · {messaging.failed_jobs} trabalhos com falha
				</span>
				<span>
					{messaging.recovered} mensagens recuperadas · {messaging.unsupported} eventos para revisar
				</span>
			</div>
		</div>
	);
}

function PipelineList({ pipeline }: { pipeline: SystemJobsOverview["pipeline"] }) {
	if (pipeline.length === 0) {
		return null;
	}

	return (
		<div data-slot="pipeline" className="flex flex-col gap-1.5">
			<p className="text-xs font-medium text-foreground">{SystemLabels.pipelineTitle}</p>

			<ul className="flex flex-wrap gap-2">
				{pipeline.map((job) => (
					<li
						key={`${job.type}-${job.status}`}
						className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
					>
						<span className="font-medium text-foreground">{job.type}</span>
						<span>{job.status}</span>
						<span className="font-mono">{job.count}</span>
					</li>
				))}
			</ul>
		</div>
	);
}
